<script setup lang="ts">
import ShowIcon from '@/assets/icons/ShowIcon.vue';
import { useField } from 'vee-validate';
import { computed, ref, useId, watch } from 'vue';

const inputDomId = useId();
const errorDomId = useId();

const emit = defineEmits<{
  'update:value': [value: string];
  blur: [];
}>();

const props = withDefaults(
  defineProps<{
    autofocus?: boolean;
    disabled?: boolean;
    inline?: boolean;
    label?: string;
    maxErrors?: number;
    name: string;
    placeholder?: string;
    type?: 'text' | 'password';
    validateOnInput?: boolean;
    value?: string;
    roundedType?: 'panel';
    maxLength?: number;
    inputAttrs?: Record<string, string>;
  }>(),
  {
    autofocus: false,
    disabled: false,
    inline: false,
    label: undefined,
    maxErrors: 100,
    placeholder: undefined,
    type: 'text',
    validateOnInput: true,
    value: undefined,
    roundedType: undefined,
    maxLength: undefined,
    inputAttrs: undefined,
  },
);

const { errors, handleChange, handleBlur, meta, value, resetField, setValue } = useField(() => props.name, undefined, {
  initialValue: props.value ?? '',
});
const inputElementRef = ref<HTMLInputElement | null>(null);

const errorsToShow = computed(() => errors.value.slice(0, props.maxErrors));
const hasErrors = computed(() => errorsToShow.value.length > 0);
const showPassword = ref(false);
const inputType = computed(() => (props.type === 'password' && !showPassword.value ? 'password' : 'text'));
const autoFocusInternal = ref<boolean>(false);

const isFocused = ref(false);

const isLabelFloating = computed(() => {
  return isFocused.value || (value.value && value.value.length > 0);
});

const handleInput = (event: Event): void => {
  handleChange(event, props.validateOnInput);
  emit('update:value', value.value);
};

watch(
  () => props.autofocus,
  () => {
    if (props.autofocus) {
      const elements = document.getElementsByName(props.name);
      if (elements.length && elements[elements.length - 1].id === inputDomId) {
        (document.activeElement as HTMLElement | null)?.blur();
        autoFocusInternal.value = props.autofocus;
      }
    }
  },
);

defineExpose({
  focus: () => inputElementRef.value?.focus(),
  resetField: () => resetField(),
  setValue: (val: string, shouldValidate = false) => setValue(val, shouldValidate),
  getValue: () => value.value,
});
</script>

<template>
  <div class="flex min-w-0 flex-col gap-sm">
    <div class="relative flex flex-col gap-xs">
      <label
        v-if="label"
        :for="inputDomId"
        class="input-text-label right-md truncate"
        :class="{ 'label-floating': isLabelFloating, 'text-tint': isFocused }"
      >
        {{ label }}
      </label>

      <input
        v-bind="inputAttrs"
        :id="inputDomId"
        ref="inputElementRef"
        :name="name"
        :disabled="disabled"
        class="input-text"
        :class="{
          'outline-inner-error': hasErrors,
          'input-text-disabled': disabled,
          'input-text-labeled': label,
          [`rounded-${props.roundedType}`]: props.roundedType !== undefined,
        }"
        :maxlength="props.maxLength"
        :autofocus="autoFocusInternal"
        :type="inputType"
        :value="value"
        :aria-required="meta.required"
        :placeholder="!label ? placeholder : ''"
        :aria-invalid="hasErrors"
        :aria-describedby="hasErrors ? errorDomId : undefined"
        :aria-errormessage="hasErrors ? errorDomId : undefined"
        @input="handleInput"
        @focus="isFocused = true"
        @blur="
          (e) => {
            isFocused = false;
            handleBlur(e);
            emit('blur');
          }
        "
      />

      <ShowIcon
        v-if="props.type === 'password'"
        class="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
        :is-showing="showPassword"
        @click="showPassword = !showPassword"
      />
    </div>

    <ul v-if="hasErrors" :id="errorDomId" class="text-sm text-right text-error">
      <li v-for="error of errorsToShow" :key="error" class="list-none">{{ error }}</li>
    </ul>

    <slot name="note"></slot>
  </div>
</template>
